import { useMemo, useState, type FormEvent } from 'react';
import type { DetectLanguagePayload, Language } from '../api/types';
import { usePostIntake } from '../hooks/usePostIntake';
import { SUPPORTED_LANGUAGES } from '../api/types';
import { tForLang, type StringKey } from '../i18n';
import { useIntake } from '../state/IntakeContext';
import { detectBrowserLanguage } from '../utils/language';
import { ThemeToggle } from './ThemeToggle';
import './WelcomeScreen.css';

/** Steps 1–5 of the citizen intake flow. Shows a simultaneous multilingual
 *  greeting, then a browser-language prompt, then sends the user's response
 *  through `detect_language` so the Worker's E4B classifier can set the
 *  active language. */
export function WelcomeScreen() {
  const { setPhase, setLanguage } = useIntake();
  const { send, loading, error } = usePostIntake();

  // Detect browser language once on mount. Never changes after.
  const browserLang = useMemo<Language>(() => detectBrowserLanguage(), []);

  // The user's typed response. Pre-populated with "Yes" in the detected
  // language when the user taps the confirmation button; free-text
  // otherwise.
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState(false);

  async function submit(payloadMessage: string) {
    setTouched(true);
    const trimmed = payloadMessage.trim();
    if (trimmed.length === 0) return;

    const payload: DetectLanguagePayload = { message: trimmed };
    try {
      const res = await send<DetectLanguagePayload>({
        action: 'detect_language',
        payload,
      });
      const resolved = res.extras?.active_language;
      if (resolved && resolved !== 'unsupported') {
        setLanguage(resolved);
        setPhase('upload');
      }
      // If 'unsupported', the Worker's fallback reply is shown by the error
      // area and the user can retype in a different language. The Worker
      // does not mutate state in that case.
    } catch {
      /* error surfaced via `error` from usePostIntake */
    }
  }

  function onYes() {
    const canned = tForLang(browserLang, 'common.yes');
    setMessage(canned);
    void submit(canned);
  }

  function onNo() {
    setMessage('');
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void submit(message);
  }

  return (
    <main id="main" className="cf-welcome-screen" lang={browserLang}>
      <header className="cf-welcome-header">
        <ThemeToggle />
      </header>

      <section className="cf-welcome-greetings" aria-label="Greeting">
        {/* Step 2: simultaneous multilingual greeting. Each greeting is
            explicitly tagged with its lang attribute so screen readers
            announce it correctly. */}
        {SUPPORTED_LANGUAGES.map((lang) => (
          <p key={lang} lang={lang} className="cf-welcome-greeting">
            {tForLang(lang, 'welcome.greeting' as StringKey)}
          </p>
        ))}
      </section>

      <section className="cf-welcome-prompt" aria-live="polite">
        <p className="cf-welcome-prompt__text">
          {tForLang(browserLang, 'welcome.language_confirm', {
            language: tForLang(browserLang, `language.${browserLang}` as StringKey),
          })}
        </p>

        <div className="cf-welcome-quick-replies" role="group">
          <button
            type="button"
            className="cf-btn cf-btn--primary"
            onClick={onYes}
            disabled={loading}
          >
            {tForLang(browserLang, 'welcome.language_confirm_yes', {
              language: tForLang(browserLang, `language.${browserLang}` as StringKey),
            })}
          </button>
          <button
            type="button"
            className="cf-btn cf-btn--ghost"
            onClick={onNo}
            disabled={loading}
          >
            {tForLang(browserLang, 'welcome.language_confirm_no')}
          </button>
        </div>

        <form className="cf-welcome-form" onSubmit={onSubmit}>
          <label className="cf-welcome-label" htmlFor="welcome-response">
            {tForLang(browserLang, 'welcome.language_ask_other')}
          </label>
          <div className="cf-welcome-input-row">
            <input
              id="welcome-response"
              type="text"
              className="cf-input"
              autoComplete="off"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
              placeholder={tForLang(browserLang, 'chat.input_placeholder')}
              aria-invalid={touched && message.trim().length === 0}
            />
            <button
              type="submit"
              className="cf-btn cf-btn--primary"
              disabled={loading || message.trim().length === 0}
            >
              {loading
                ? tForLang(browserLang, 'common.loading')
                : tForLang(browserLang, 'common.continue')}
            </button>
          </div>
        </form>

        {error && (
          <p className="cf-welcome-error" role="alert">
            {/* Symbol + text — never color alone. */}
            <span aria-hidden="true">⚠</span>{' '}
            {tForLang(browserLang, 'chat.error_network')}
          </p>
        )}
      </section>
    </main>
  );
}
